(() => {
  const runtimeKey = '__guruBrandsRuntime';
  window[runtimeKey]?.destroy?.();

  const instances = new Map();
  const lifecycleController = new AbortController();
  const lifecycleSignal = lifecycleController.signal;

  const init = (section) => {
    if (!(section instanceof HTMLElement) || instances.has(section)) return;

    const video = section.querySelector('[data-guru-brands-video]');
    if (!(video instanceof HTMLVideoElement)) return;

    const sources = [...video.querySelectorAll('source[data-src]')];
    const motionQuery = window.matchMedia('(prefers-reduced-motion: reduce)');
    const controller = new AbortController();
    const { signal } = controller;
    let observer;
    let inView = false;
    let ready = false;
    let sourceAssigned = false;

    const unload = () => {
      video.pause();
      sources.forEach((source) => source.removeAttribute('src'));
      video.load();
      sourceAssigned = false;
      ready = false;
    };

    const load = () => {
      if (sourceAssigned || motionQuery.matches) return;
      sources.forEach((source) => source.setAttribute('src', source.dataset.src || ''));
      sourceAssigned = true;
      video.load();
    };

    const play = () => {
      if (!ready || !inView || motionQuery.matches) return;
      video.play().catch(() => {});
    };

    const reconcile = () => {
      if (motionQuery.matches) {
        if (sourceAssigned) unload();
        return;
      }

      if (inView) {
        load();
        play();
      } else {
        video.pause();
      }
    };

    video.addEventListener('loadeddata', () => {
      ready = true;
      play();
    }, { signal });

    if ('IntersectionObserver' in window) {
      observer = new IntersectionObserver(([entry]) => {
        inView = entry.isIntersecting;
        reconcile();
      }, { threshold: 0.2 });
      observer.observe(section);
    } else {
      inView = true;
    }

    if (typeof motionQuery.addEventListener === 'function') {
      motionQuery.addEventListener('change', reconcile);
    } else {
      motionQuery.addListener?.(reconcile);
    }

    const instance = {
      destroy() {
        controller.abort();
        observer?.disconnect();
        if (typeof motionQuery.removeEventListener === 'function') {
          motionQuery.removeEventListener('change', reconcile);
        } else {
          motionQuery.removeListener?.(reconcile);
        }
        unload();
        instances.delete(section);
      },
    };
    instances.set(section, instance);
    reconcile();
  };

  const initAll = (root = document) => {
    if (root instanceof HTMLElement && root.matches('[data-guru-brands-origin]')) init(root);
    root.querySelectorAll?.('[data-guru-brands-origin]').forEach(init);
  };

  const destroyWithin = (root) => {
    const sections = [];
    if (root instanceof HTMLElement && root.matches('[data-guru-brands-origin]')) sections.push(root);
    root.querySelectorAll?.('[data-guru-brands-origin]').forEach((section) => sections.push(section));
    sections.forEach((section) => instances.get(section)?.destroy());
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => initAll(), { once: true, signal: lifecycleSignal });
  } else {
    initAll();
  }
  document.addEventListener('shopify:section:load', (event) => initAll(event.target), { signal: lifecycleSignal });
  document.addEventListener('shopify:section:unload', (event) => destroyWithin(event.target), { signal: lifecycleSignal });

  window[runtimeKey] = {
    destroy() {
      lifecycleController.abort();
      [...instances.values()].forEach((instance) => instance.destroy());
      instances.clear();
      if (window[runtimeKey] === this) delete window[runtimeKey];
    },
  };
})();
